import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';
import ReactDOM from 'react-dom';
import { usePasswordForm } from '@odo/shared-ui/hooks/usePasswordForm';
import checkIcon from '../../assets/icons/check.svg';
import xMarkIcon from '../../assets/icons/x-mark.svg';

interface PasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (currentPassword: string, newPassword: string) => void;
}

const PasswordModal: React.FC<PasswordModalProps> = ({ isOpen, onClose, onSave }) => {
    const {
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        showCurrent,
        setShowCurrent,
        showNew,
        setShowNew,
        showConfirm,
        setShowConfirm,
        rules,
        score,
        strengthColor,
        allValid
    } = usePasswordForm(isOpen);

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Set new password</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            ></path>
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">
                            Current password
                        </label>
                        <div className="relative">
                            <input
                                type={showCurrent ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrent((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                            >
                                {showCurrent ? (
                                    <EyeIcon className="w-4 h-4" />
                                ) : (
                                    <EyeSlashIcon className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">New password</label>
                        <div className="relative">
                            <input
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                            >
                                {showNew ? (
                                    <EyeIcon className="w-4 h-4" />
                                ) : (
                                    <EyeSlashIcon className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">
                            Confirm new password
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Re-enter new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                            >
                                {showConfirm ? (
                                    <EyeIcon className="w-4 h-4" />
                                ) : (
                                    <EyeSlashIcon className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Strength meter */}
                    <div className="mt-1">
                        <p className="text-xs font-semibold text-gray-500 mb-1">
                            Password strength
                        </p>
                        <div className="flex gap-1" aria-label="Password strength">
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 flex-1 rounded ${idx < score ? strengthColor : 'bg-gray-200'}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-2">
                        <p className="text-xs font-semibold text-gray-500 mb-2">
                            Your password must contain:
                        </p>
                        <ul className="space-y-2">
                            {rules.map((rule) => (
                                <li key={rule.id} className="flex items-center gap-2 text-sm">
                                    <img
                                        src={rule.valid ? checkIcon : xMarkIcon}
                                        alt="status"
                                        className="w-4 h-4"
                                    />
                                    <span
                                        className={rule.valid ? 'text-gray-700' : 'text-gray-500'}
                                    >
                                        {rule.label}
                                    </span>
                                </li>
                            ))}
                            <li className="flex items-center gap-2 text-sm">
                                <img
                                    src={
                                        newPassword === confirmPassword && newPassword.length > 0
                                            ? checkIcon
                                            : xMarkIcon
                                    }
                                    alt="status"
                                    className="w-4 h-4"
                                />
                                <span
                                    className={
                                        newPassword === confirmPassword && newPassword.length > 0
                                            ? 'text-gray-700'
                                            : 'text-gray-500'
                                    }
                                >
                                    Passwords match
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!allValid}
                        onClick={() => onSave(currentPassword, newPassword)}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg ${allValid ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};

export default PasswordModal;
